import React, { useState, useEffect } from "react";
import { bookmarksApi } from "../lib/api";
import { toast } from "sonner";
import { Archive, Bookmark } from "@phosphor-icons/react";
import { Badge } from "../components/ui/badge";
import IntelligenceWorkbench from "../components/workbench/IntelligenceWorkbench";

const SIGNAL_BORDER = {
  trend: "border-l-teal-500", opportunity: "border-l-emerald-500",
  anomaly: "border-l-amber-500", contrarian: "border-l-rose-500",
};

export default function VaultPage() {
  const [snaps,      setSnaps]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [workbenchId,setWorkbenchId]= useState(null);
  const [bookmarks,  setBookmarks]  = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await bookmarksApi.snapshots();
      setSnaps(data);
      setBookmarks(new Set(data.map((s) => s.id)));
    } catch { toast.error("Failed to load vault"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const open  = (snap) => setWorkbenchId(snap.id);
  const close = () => setWorkbenchId(null);

  const removeBookmark = async (snap) => {
    try {
      await bookmarksApi.remove(snap.id);
      setSnaps((s) => s.filter((x) => x.id !== snap.id));
      setBookmarks((b) => { const n = new Set(b); n.delete(snap.id); return n; });
      toast.success("Removed from vault");
    } catch { toast.error("Failed to remove"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <div className="mb-8">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-1">Vault</p>
        <h1 className="font-display text-[22px] font-bold text-slate-900 tracking-tight">
          Saved Intel <span className="text-slate-400 font-normal">({snaps.length})</span>
        </h1>
        <p className="text-[13px] text-slate-500 mt-1">
          Curated intelligence. Bookmarked signals stay provenance-anchored to their source evidence.
        </p>
      </div>

      {loading ? (
        <div className="text-xs text-slate-400 py-12 text-center">Loading vault…</div>
      ) : snaps.length === 0 ? (
        <div className="text-center py-16">
          <Archive size={28} className="text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Your vault is empty.</p>
          <p className="text-xs text-slate-400 mt-1">
            Bookmark signals from the Trend Radar to save them here.
          </p>
        </div>
      ) : (
        <div className="grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {snaps.map((snap) => (
            <article
              key={snap.id}
              className={`border border-slate-200 border-l-4 ${SIGNAL_BORDER[snap.signal_type] || "border-l-emerald-500"} bg-white rounded-lg cursor-pointer hover:shadow-md transition-all duration-150`}
              onClick={() => open(snap)}
            >
              <div className="px-4 py-4">
                <div className="flex items-center gap-2 mb-2.5">
                  <Badge variant={snap.signal_type || "opportunity"}>
                    {(snap.signal_type || "OPPORTUNITY").toUpperCase()}
                  </Badge>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeBookmark(snap); }}
                    className="ml-auto p-1 rounded hover:bg-slate-100"
                    title="Remove from vault"
                  >
                    <Bookmark size={14} weight="fill" className="text-slate-500" />
                  </button>
                </div>
                <h3 className="text-[15px] font-semibold text-slate-900 leading-snug mb-1.5">{snap.title}</h3>
                <p className="text-[13px] text-slate-600 leading-relaxed line-clamp-3 mb-3">{snap.summary}</p>
                <div className="flex gap-1.5 flex-wrap items-center">
                  {(snap.tags || []).slice(0, 3).map((t) => (
                    <span key={t} className="text-[11px] font-mono uppercase text-slate-500 bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded">{t}</span>
                  ))}
                  <span className="ml-auto font-mono text-[11px] text-slate-400">
                    {new Date(snap.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {workbenchId && (
        <IntelligenceWorkbench
          snapshotId={workbenchId}
          onClose={close}
          bookmarked={bookmarks.has(workbenchId)}
          onBookmarkToggle={removeBookmark}
        />
      )}
    </div>
  );
}
